// src/components/opportunity/OpportunityPricingForm.ts
//
// Form-state shape + helpers for the Pricing tab. Keeps the tab
// component focused on JSX while the unit-tested calc helpers in
// `src/lib/opportunity/calc.ts` stay numeric.
//
// `numeric` form fields are stored as raw strings because the user is
// typing — we only coerce to numbers when we feed `computePricingTotals`.

import type {
  OpportunityAdditionalItem,
  OpportunityAmountMode,
  OpportunityFormatKey,
  OpportunityPricingInputs,
  OpportunityPricingSnapshot,
  OpportunitySalesAdjustment,
} from "@/types/opportunity";

export type OpportunityPricingFormItem = {
  /** Local stable key for React lists; not persisted. */
  key: string;
  label: string;
  amount: string;
};

export type OpportunityPricingFormAdjustment = {
  key: string;
  description: string;
  amountMode: OpportunityAmountMode;
  value: string;
};

export type OpportunityPricingFormState = {
  formatKey: OpportunityFormatKey;
  // Red Iron form fields (also persisted as 0 when format=generic).
  buildingMaterialsCost: string;
  erectionCost: string;
  concreteCost: string;
  engineeringCost: string;
  permitAssistanceCost: string;
  additionalItems: OpportunityPricingFormItem[];
  commissionPct: string;
  marginPct: string;
  depositPct: string;
  // Generic format fields.
  subtotalInput: string;
  taxMode: OpportunityAmountMode;
  taxValue: string;
  depositMode: OpportunityAmountMode;
  depositAmountInput: string;
  salesAdjustments: OpportunityPricingFormAdjustment[];
  depositDeviationNote: string;
  formatCalcValues: Record<string, string>;
  /** Per-line $/% override for manager calc lines (line id → mode). */
  formatCalcModes: Record<string, "fixed" | "percent">;
  /** Per-line required note when a percent line is forced to dollars. */
  formatCalcDeviationNotes: Record<string, string>;
  notes: string;
};

let nextKey = 1;
function genKey(): string {
  // Suffix with `Math.random()` so two items created in the same
  // event-loop tick still get distinct keys (the integer alone is
  // enough but the random tail makes hot-reload survive).
  nextKey += 1;
  return `item-${nextKey}-${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyOpportunityPricingFormState(opts?: {
  formatKey?: OpportunityFormatKey;
  commissionPct?: number | null;
  marginPct?: number | null;
}): OpportunityPricingFormState {
  return {
    formatKey: opts?.formatKey ?? "red_iron",
    buildingMaterialsCost: "",
    erectionCost: "",
    concreteCost: "",
    engineeringCost: "",
    permitAssistanceCost: "",
    additionalItems: [],
    commissionPct:
      opts?.commissionPct != null && Number.isFinite(opts.commissionPct)
        ? String(opts.commissionPct)
        : "",
    marginPct:
      opts?.marginPct != null && Number.isFinite(opts.marginPct) ? String(opts.marginPct) : "",
    depositPct: "25",
    subtotalInput: "",
    taxMode: "percent",
    taxValue: "",
    depositMode: "percent",
    depositAmountInput: "",
    salesAdjustments: [],
    depositDeviationNote: "",
    formatCalcValues: {},
    formatCalcModes: {},
    formatCalcDeviationNotes: {},
    notes: "",
  };
}

export function formFromSnapshot(
  snapshot: OpportunityPricingSnapshot,
): OpportunityPricingFormState {
  // Older snapshots persisted before slice B don't carry the generic
  // columns; treat missing values as the red_iron defaults so the form
  // doesn't render undefined into an input. The `?? "red_iron"` here
  // mirrors `snapshotFormatKey` for callers that didn't go through it.
  const formatKey: OpportunityFormatKey =
    snapshot.format_key === "generic" ? "generic" : "red_iron";
  return {
    formatKey,
    buildingMaterialsCost: numToInputString(snapshot.building_materials_cost),
    erectionCost: numToInputString(snapshot.erection_cost),
    concreteCost: numToInputString(snapshot.concrete_cost),
    engineeringCost: numToInputString(snapshot.engineering_cost),
    permitAssistanceCost: numToInputString(snapshot.permit_assistance_cost),
    additionalItems: (snapshot.additional_items ?? []).map((it) => ({
      key: genKey(),
      label: it.label ?? "",
      amount: numToInputString(it.amount),
    })),
    commissionPct: numToInputString(snapshot.commission_pct),
    marginPct: numToInputString(snapshot.margin_pct),
    depositPct: numToInputString(snapshot.deposit_pct),
    subtotalInput: numToInputString(snapshot.subtotal_input ?? 0),
    taxMode: snapshot.tax_mode === "percent" ? "percent" : "amount",
    taxValue: numToInputString(snapshot.tax_value ?? 0),
    depositMode: snapshot.deposit_mode === "amount" ? "amount" : "percent",
    // The deposit input field stores the dollar amount when mode=amount;
    // for older red_iron snapshots, seed it from the persisted
    // deposit_amount so flipping to mode=amount in the UI starts at the
    // last-saved value rather than 0.
    depositAmountInput: numToInputString(snapshot.deposit_amount ?? 0),
    salesAdjustments: (snapshot.sales_adjustments ?? []).map((adj) => ({
      key: genKey(),
      description: adj.description ?? "",
      amountMode: adj.amount_mode === "percent" ? "percent" : "amount",
      value: numToInputString(adj.value),
    })),
    depositDeviationNote: snapshot.deposit_deviation_note ?? "",
    formatCalcValues: Object.fromEntries(
      Object.entries(snapshot.format_calc_values ?? {}).map(([id, amount]) => [
        id,
        numToInputString(amount),
      ]),
    ),
    formatCalcModes: { ...(snapshot.format_calc_modes ?? {}) },
    formatCalcDeviationNotes: { ...(snapshot.format_calc_deviation_notes ?? {}) },
    // The snapshot doesn't carry notes (they live on the revision row),
    // and revision notes describe a *change* — we don't want to
    // pre-populate the new revision's notes with the old one.
    notes: "",
  };
}

export function makeAdditionalItem(): OpportunityPricingFormItem {
  return { key: genKey(), label: "", amount: "" };
}

export function makeSalesAdjustment(): OpportunityPricingFormAdjustment {
  return { key: genKey(), description: "", amountMode: "amount", value: "" };
}

export function pricingInputsFromForm(form: OpportunityPricingFormState): OpportunityPricingInputs {
  const items: OpportunityAdditionalItem[] = form.additionalItems
    .map((it) => ({ label: it.label.trim(), amount: numFromInput(it.amount) }))
    .filter((it) => it.label.length > 0 || it.amount !== 0);
  const salesAdjustments: OpportunitySalesAdjustment[] = form.salesAdjustments
    .map((adj) => ({
      description: adj.description.trim(),
      amount_mode: adj.amountMode,
      value: numFromInput(adj.value),
    }))
    .filter((adj) => adj.description.length > 0 || adj.value !== 0);
  return {
    format_key: form.formatKey,
    building_materials_cost: numFromInput(form.buildingMaterialsCost),
    erection_cost: numFromInput(form.erectionCost),
    concrete_cost: numFromInput(form.concreteCost),
    engineering_cost: numFromInput(form.engineeringCost),
    permit_assistance_cost: numFromInput(form.permitAssistanceCost),
    additional_items: items,
    commission_pct: numFromInput(form.commissionPct),
    margin_pct: numFromInput(form.marginPct),
    deposit_pct: numFromInput(form.depositPct),
    subtotal_input: numFromInput(form.subtotalInput),
    tax_mode: form.taxMode,
    tax_value: numFromInput(form.taxValue),
    deposit_mode: form.depositMode,
    deposit_amount_input: numFromInput(form.depositAmountInput),
    sales_adjustments: salesAdjustments,
    deposit_deviation_note: form.depositDeviationNote.trim() || null,
    format_calc_values: Object.fromEntries(
      Object.entries(form.formatCalcValues)
        .filter(([, raw]) => raw.trim().length > 0)
        .map(([id, raw]) => [id, numFromInput(raw)]),
    ),
    format_calc_modes: { ...form.formatCalcModes },
    format_calc_deviation_notes: Object.fromEntries(
      Object.entries(form.formatCalcDeviationNotes)
        .map(([id, note]) => [id, note.trim()])
        .filter(([, note]) => note.length > 0),
    ),
  };
}

/** Stable fingerprint for dirty-checking Pricing edits vs last saved snapshot. */
export function pricingFormFingerprint(form: OpportunityPricingFormState): string {
  return JSON.stringify({
    inputs: pricingInputsFromForm(form),
    notes: form.notes.trim(),
  });
}

function numFromInput(s: string): number {
  if (!s) return 0;
  const cleaned = s.replace(/[$,\s]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function numToInputString(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "";
  // Trim trailing zeros so `25.00` shows as `25` in the input but
  // `25.50` keeps its precision.
  const fixed = Number(n).toFixed(2);
  return fixed.replace(/\.00$/, "").replace(/(\.\d*?)0+$/, "$1");
}
